import { useEffect, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import PageHeader from '../common/PageHeader/PageHeader';
import Button from '../common/Button/Button';
import PortalModal from '../common/PortalModal/PortalModal';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import './Organization.css';

export default function WorkspaceManagement() {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [memberInput, setMemberInput] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getWorkspaces();
      const nextWorkspaces = response?.workspaces || [];
      setWorkspaces(nextWorkspaces);
      return nextWorkspaces;
    } catch (error) {
      console.error('Error loading workspaces:', error);
      setWorkspaces([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedWorkspace = async (workspaceId) => {
    const latestWorkspaces = await loadWorkspaces();
    const updatedWorkspace = latestWorkspaces.find(
      ws => (ws._id || ws.id) === workspaceId
    ) || null;
    setSelectedWorkspace(updatedWorkspace);
    return updatedWorkspace;
  };

  const openMembersModal = (workspace) => {
    setSelectedWorkspace(workspace);
    setMemberInput('');
    setAvailableUsers([]);
    setShowMembersModal(true);
  };

  const openEditModal = (workspace) => {
    setSelectedWorkspace(workspace);
    setEditForm({
      name: workspace.name || '',
      description: workspace.description || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateWorkspace = async () => {
    if (!editForm.name.trim()) {
      alert('Workspace name is required.');
      return;
    }
    try {
      const workspaceId = selectedWorkspace._id || selectedWorkspace.id;
      await apiClient.updateWorkspace(workspaceId, {
        name: editForm.name.trim(),
        description: editForm.description.trim()
      });
      setShowEditModal(false);
      await loadWorkspaces();
    } catch (error) {
      console.error('Error updating workspace:', error);
      alert(`Failed to update workspace: ${error.message}`);
    }
  };

  useEffect(() => {
    const fetchAvailableUsers = async () => {
      if (!showMembersModal || !selectedWorkspace) {
        return;
      }
      const workspaceId = selectedWorkspace._id || selectedWorkspace.id;
      if (!workspaceId) {
        return;
      }
      setLoadingUsers(true);
      try {
        const response = await apiClient.getAvailableWorkspaceUsers(workspaceId);
        console.log('Available users API response:', response);
        setAvailableUsers(response?.users || []);
      } catch (error) {
        console.error('Error loading available users:', error);
        setAvailableUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAvailableUsers();
  }, [showMembersModal, selectedWorkspace]);

  const handleAddMember = async () => {
    if (!selectedWorkspace) {
      return;
    }
    const value = memberInput.trim();
    if (!value) {
      alert('Please select a user.');
      return;
    }

    try {
      const workspaceId = selectedWorkspace._id || selectedWorkspace.id;
      await apiClient.addWorkspaceMember(workspaceId, value);
      setMemberInput('');
      await refreshSelectedWorkspace(workspaceId);
    } catch (error) {
      console.error('Error adding member:', error);
      alert(`Failed to add member: ${error.message}`);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!selectedWorkspace || !member) {
      return;
    }
    const confirmed = window.confirm(`Remove member "${member}" from this workspace?`);
    if (!confirmed) {
      return;
    }

    try {
      const workspaceId = selectedWorkspace._id || selectedWorkspace.id;
      await apiClient.removeWorkspaceMember(workspaceId, member);
      setMemberInput('');
      await refreshSelectedWorkspace(workspaceId);
    } catch (error) {
      console.error('Error removing member:', error);
      alert(`Failed to remove member: ${error.message}`);
    }
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    if (!workspaceId) {
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to delete this workspace? Documents and memories will remain, but will lose this grouping.'
    );
    if (!confirmed) {
      return;
    }
    try {
      await apiClient.deleteWorkspace(workspaceId);
      await loadWorkspaces();
      if ((selectedWorkspace?._id || selectedWorkspace?.id) === workspaceId) {
        setSelectedWorkspace(null);
      }
    } catch (error) {
      console.error('Error deleting workspace:', error);
      alert(`Failed to delete workspace: ${error.message}`);
    }
  };

  const membersFooter = (
    <>
      <Button variant="secondary" onClick={() => setShowMembersModal(false)}>
        Close
      </Button>
      <Button variant="primary" onClick={handleAddMember}>
        Add Member
      </Button>
    </>
  );

  return (
    <div className="section-content">
      <PageHeader
        title="Workspaces"
        description="Create and manage workspace access scopes for documents and memories."
        icon="fas fa-layer-group"
        accentColor="teal"
        actions={(
          <Button
            variant="primary"
            icon="fas fa-plus"
            onClick={() => setShowCreateModal(true)}
          >
            Create Workspace
          </Button>
        )}
      />

      {loading ? (
        <p>Loading workspaces...</p>
      ) : (
        <div className="sources-list">
          {workspaces.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-layer-group"></i>
              <h3>No workspaces yet</h3>
              <p>Create a workspace to organize access across teams.</p>
            </div>
          ) : (
            workspaces.map(workspace => (
              <div
                key={workspace._id || workspace.id}
                className="source-card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  padding: '1.5rem',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-blue)' }}>
                      <i className="fas fa-layer-group" style={{ fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: 'var(--text-primary)' }}>{workspace.name}</h3>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        Created {workspace.created_at ? new Date(workspace.created_at).toLocaleDateString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="fas fa-edit"
                      onClick={() => openEditModal(workspace)}
                      title="Edit Workspace"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="fas fa-users"
                      onClick={() => openMembersModal(workspace)}
                    >
                      Members
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      icon="fas fa-trash"
                      onClick={() => handleDeleteWorkspace(workspace._id || workspace.id)}
                      title="Delete Workspace"
                    />
                  </div>
                </div>

                <div style={{ padding: '0 4px' }}>
                  {workspace.description ? (
                    <p style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                      {workspace.description}
                    </p>
                  ) : (
                    <p style={{ margin: '0 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      No description provided.
                    </p>
                  )}

                  {(workspace.include_public_context || (workspace.linked_workspaces && workspace.linked_workspaces.length > 0)) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginRight: '4px' }}>
                        Inherits from:
                      </span>
                      {workspace.include_public_context && (
                        <span style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--accent-teal, #10b981)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <i className="fas fa-building"></i> Company Context
                        </span>
                      )}
                      {(workspace.linked_workspaces || []).map(linkedId => {
                        const linkedWs = workspaces.find(w => (w._id || w.id) === linkedId);
                        return (
                          <span key={linkedId} style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'var(--bg-surface-hover)', color: 'var(--text-secondary)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--border-subtle)' }}>
                            <i className="fas fa-link"></i> {linkedWs ? linkedWs.name : 'Unknown Workspace'}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '16px', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title="Workspace Creator">
                    <i className="fas fa-user-tie"></i>
                    <span>Owner: <strong>{workspace.owner_name || 'System'}</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title="Total Members">
                    <i className="fas fa-users"></i>
                    <span>{workspace.members ? workspace.members.length : 0} Members</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateWorkspaceModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={loadWorkspaces}
        />
      )}

      {showEditModal && selectedWorkspace && (
        <PortalModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Workspace"
          size="medium"
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleUpdateWorkspace}>Save Changes</Button>
            </>
          }
        >
          <div className="modal-body">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                className="form-control"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label>Description</label>
              <textarea
                className="form-control"
                rows="4"
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe this workspace"
              />
            </div>
          </div>
        </PortalModal>
      )}

      {showMembersModal && selectedWorkspace && (
        <PortalModal
          isOpen={showMembersModal}
          onClose={() => setShowMembersModal(false)}
          title={`Manage Members - ${selectedWorkspace.name}`}
          size="medium"
          footer={membersFooter}
        >
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Select User to Add</label>
              <select
                className="form-control"
                value={memberInput}
                onChange={(event) => setMemberInput(event.target.value)}
              >
                <option value="">-- Select a User --</option>
                {availableUsers.map((user) => (
                  <option key={user.user_sub} value={user.user_sub}>
                    {user.name ? `${user.name} (${user.email})` : user.email || user.user_sub}
                  </option>
                ))}
              </select>
            </div>
            {loadingUsers && (
              <p className="muted-text">Loading available users...</p>
            )}
            <div className="form-group">
              <label>Current Members</label>
              <div className="members-list-container">
                <div
                  className="member-row"
                  style={{
                    backgroundColor: 'var(--bg-surface-hover, rgba(255,255,255,0.05))',
                    borderLeft: '3px solid var(--accent-blue, #3b82f6)'
                  }}
                >
                  <div className="member-details">
                    <span className="member-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {selectedWorkspace.owner_name || 'Workspace Owner'}
                      <span
                        style={{
                          fontSize: '0.7em',
                          padding: '2px 6px',
                          backgroundColor: 'var(--accent-blue, #3b82f6)',
                          color: 'white',
                          borderRadius: '4px',
                          fontWeight: 'bold'
                        }}
                      >
                        Owner
                      </span>
                    </span>
                    <span className="member-email">Workspace Creator</span>
                  </div>
                  <div style={{ padding: '5px', width: '24px' }}></div>
                </div>
                {(selectedWorkspace.members_info || [])
                  .filter(member => member.sub !== (selectedWorkspace.owner_id || selectedWorkspace.ownerId))
                  .map((member) => (
                  <div key={member.sub} className="member-row">
                    <div className="member-details">
                      <span className="member-name" style={{ fontWeight: '500' }}>
                        {member.name || member.email || member.sub}
                      </span>
                      {member.email && member.email !== member.name && (
                        <span className="member-email" style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                          {member.email}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-icon text-danger"
                      onClick={() => handleRemoveMember(member.sub)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                      title="Remove Member"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                ))}
              </div>
              {(selectedWorkspace.members_info || [])
                .filter(member => member.sub !== (selectedWorkspace.owner_id || selectedWorkspace.ownerId))
                .length === 0 && (
                <ul className="simple-list">
                  <li>No additional members yet</li>
                </ul>
              )}
            </div>
          </div>
        </PortalModal>
      )}
    </div>
  );
}
